import React, { useState, createContext, useContext, useEffect } from "react";
import { useApiService } from "../shared/apiservice";
import { ExpectedDelivery } from "../shared/types";

const expectedDeliveriesContext = createContext<{
    expectedDeliveries: ExpectedDelivery[];
    errors: string[];
    loading: boolean;
    fetchDeliveries: () => void;
}>({
    expectedDeliveries: [],
    errors: [],
    loading: false,
    fetchDeliveries: () => { },
});

function parseDeliveryLine(d: any) {
    return {
        ...d,
    };
}

function parseDelivery(d: any): ExpectedDelivery {
    return {
        ...d,
        completed_at: d.completed_at ? new Date(d.completed_at) : undefined,
        placed_at: d.placed_at ? new Date(d.placed_at) : undefined,
        amended_at: d.amended_at ? new Date(d.amended_at) : undefined,
        delivery_date: d.delivery_date ? new Date(d.delivery_date) : undefined,
        lines: (d.lines ?? []).map(parseDeliveryLine),
    };
}

function useExpectedDeliveries(date: Date) {
    const [expectedDeliveries, setExpectedDeliveries] = useState<ExpectedDelivery[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const apiService = useApiService();

    useEffect(() => {
        setExpectedDeliveries([]);
        setErrors([]);
        fetchDeliveries();
    }, [date]);

    async function fetchDeliveries() {
        setLoading(true);
        setErrors([]);

        try {
            const response = await apiService.get(`/expected-deliveries/for-date/${date.toISOString().slice(0, 10)}`);
            if (!response.ok) {
                setErrors(["Loading expected deliveries failed. Please try again."]);
                return;
            }
            const json = await response.json();
            const data: ExpectedDelivery[] = (json.data ?? json).map(parseDelivery);
            setExpectedDeliveries(data);
        } catch (error) {
            console.error(error);
            setErrors(["Loading expected deliveries failed. Please try again."]);
        } finally {
            setLoading(false);
        }
    }

    return { expectedDeliveries, errors, loading, fetchDeliveries };
}

export function ProviderExpectedDeliveries({
    children,
    date,
}: {
    children: React.ReactNode;
    date: Date;
}) {
    const value = useExpectedDeliveries(date);
    return <expectedDeliveriesContext.Provider value={value}>{children}</expectedDeliveriesContext.Provider>;
}

export const useExpectedDeliveriesContext = () => {
    return useContext(expectedDeliveriesContext);
};

export function useExpectedDeliveryDetail(id: string | undefined) {
    const [delivery, setDelivery] = useState<ExpectedDelivery | null>(null);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const apiService = useApiService();

    useEffect(() => {
        if (id) {
            fetchDelivery();
        }
    }, [id]);

    async function fetchDelivery() {
        setLoading(true);
        setErrors([]);

        try {
            const response = await apiService.get(`/expected-deliveries/${id}`);
            if (!response.ok) {
                setErrors(["Loading expected delivery failed. Please try again."]);
                return;
            }
            const json = await response.json();
            setDelivery(parseDelivery(json.data ?? json));
        } catch (error) {
            console.error(error);
            setErrors(["Loading expected delivery failed. Please try again."]);
        } finally {
            setLoading(false);
        }
    }

    return { delivery, errors, loading, fetchDelivery };
}
